import { create } from "zustand";
import { PublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { msalConfig, loginRequest, tokenRequest } from "../../config/auth";
import { logToken } from "../utils/jwt";

// User information from Azure AD
export interface User {
  name: string;
  email: string;
  username?: string;
  tenantId?: string;
}

interface AuthState {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // MSAL instance (stored for token acquisition)
  msalInstance: PublicClientApplication | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setMsalInstance: (instance: PublicClientApplication) => void;

  // Auth operations
  login: () => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
}

// Helper to extract user info from MSAL account
const extractUserInfo = (account: AccountInfo | null): User | null => {
  if (!account) return null;

  return {
    name: account.name || account.username || "Unknown User",
    email: account.username || "", // username in MSAL is typically the email
    username: account.username,
    tenantId: account.tenantId,
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start as loading to prevent flicker during initial auth check
  error: null,
  msalInstance: null,

  // Setters
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  setMsalInstance: (msalInstance) => set({ msalInstance }),

  // Initialize: check if user is already logged in from cache
  initialize: async () => {
    const { msalInstance } = get();
    if (!msalInstance) {
      console.error("[Auth] MSAL instance not initialized");
      return;
    }

    set({ isLoading: true, error: null });

    try {
      console.log("[Auth] Checking for cached session...");

      // Prefer active account (set during login), fallback to first cached account
      const activeAccount = msalInstance.getActiveAccount();
      const account = activeAccount || msalInstance.getAllAccounts()[0];

      if (account) {
        // User is already logged in (from previous session)
        if (!activeAccount) {
          msalInstance.setActiveAccount(account);
        }
        const user = extractUserInfo(account);
        set({ user, isAuthenticated: true });

        console.log("[Auth] Found cached session for:", user?.email, "tenant:", user?.tenantId);

        // LOG TOKEN: Try to acquire token silently from cache
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account,
          });
          logToken(tokenResponse.accessToken, "CACHED SESSION");
        } catch (tokenError) {
          console.warn(
            "[Auth] Could not acquire token from cache:",
            tokenError
          );
        }
      } else {
        console.log("[Auth] No cached session found");
      }
    } catch (error) {
      console.error("[Auth] Error during initialization:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to initialize authentication",
      });
    } finally {
      set({ isLoading: false });
    }
  },

  // Login: open Microsoft login popup
  login: async () => {
    const { msalInstance } = get();
    if (!msalInstance) {
      set({ error: "MSAL instance not initialized" });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Use popup flow (simpler than redirect for SPAs)
      const response = await msalInstance.loginPopup(loginRequest);

      // Set user immediately after successful popup login
      if (response && response.account) {
        msalInstance.setActiveAccount(response.account);
        const user = extractUserInfo(response.account);
        set({ user, isAuthenticated: true, isLoading: false });

        // LOG TOKEN: Acquired after popup login
        if (response.accessToken) {
          logToken(response.accessToken, "LOGIN POPUP");
        }

        console.log("[Auth] User authenticated via popup:", user?.email, "tenant:", user?.tenantId);
      }
    } catch (error) {
      console.error("Login error:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to login",
        isLoading: false,
      });
    }
  },

  // Logout: clear session via popup
  logout: async () => {
    const { msalInstance } = get();
    if (!msalInstance) {
      set({ error: "MSAL instance not initialized" });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Get current account before logout
      const account = msalInstance.getActiveAccount();

      // Clear all accounts via popup
      await msalInstance.logoutRedirect({
        account: account || undefined,
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });

      // Reset state immediately after popup closes
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      console.log("[Auth] User logged out via popup");
    } catch (error) {
      console.error("Logout error:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to logout",
        isLoading: false,
      });
    }
  },
}));
